import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import adminService from '../../../../services/adminService';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import './AdminDetails.css';

const AdminDetails = () => {
    const { adminId } = useParams();
    const navigate = useNavigate();
    const { updateBreadcrumbs } = useBreadcrumb();
    const [admin, setAdmin] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    console.log('AdminDetails Render Cycle:', { adminId, loading, error, admin });

    useEffect(() => {
        const fetchAdminDetails = async () => {
            try {
                setLoading(true);
                // Try fetching system admin details (since this is likely a system admin context based on path)
                // If it was a practice admin, the route might be different, but let's assume system admin for top level /admin/:id
                // Or we might need to handle both. For now, following System Caregiver pattern.

                // Try fetching as system admin first
                let response;
                try {
                    response = await adminService.getSystemAdmin(adminId);
                } catch (err) {
                    // If failed, it might be a practice admin, but usually we'd know from context.
                    // For now let's assume it works or we handle error.
                    console.error("Failed to fetch system admin", err);
                    // Fallback strategy if needed
                }

                let adminData = null;
                if (response?.data?.data) {
                    adminData = response.data.data;
                } else if (response?.data) {
                    adminData = response.data;
                }

                // If not found as system admin, maybe try general getAdmin if we had practiceId, but we don't here.
                // Assuming unique IDs or system admin context.

                setAdmin(adminData);
            } catch (err) {
                setError('Failed to fetch admin details');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (adminId) {
            fetchAdminDetails();
        }
    }, [adminId]);

    // Update Breadcrumbs
    useEffect(() => {
        const adminName = admin
            ? `${admin.first_name || ''} ${admin.last_name || ''}`.trim() || admin.username || 'Admin Details'
            : 'Admin Details';

        updateBreadcrumbs([
            { label: 'Admin', path: '/admin' },
            { label: adminName, path: `/admin/${adminId}` }
        ]);
    }, [admin, adminId, updateBreadcrumbs]);

    const getStatusBadge = (status) => {
        const isActive = status === 'active' || status === 'Active';
        return (
            <span className={`admin-details-status-badge ${isActive ? 'admin-details-status-active' : 'admin-details-status-inactive'}`}>
                {status || 'N/A'}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="admin-details-spinner-container">
                <div className="admin-details-spinner" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-details-container">
                <div className="admin-details-alert admin-details-alert-danger" role="alert">
                    {error}
                    <button className="admin-details-alert-btn" onClick={() => navigate('/admin')}>Go Back</button>
                </div>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="admin-details-container">
                {/* Header */}
                <div className="admin-details-header">
                    {/* No Page Title here as per request pattern */}
                    <div></div>
                    <button
                        className="admin-details-back-btn"
                        onClick={() => navigate('/admin')}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Back
                    </button>
                </div>

                {/* Admin Details Card */}
                <div className="admin-details-card">
                    <div className="admin-details-top-header">
                        <h5>Admin Details</h5>
                    </div>

                    <div className="admin-details-info-grid">
                        {/* Column 1 */}
                        <div className="admin-details-info-column">
                            <div className="admin-details-info-row">
                                <span className="admin-details-label">Name:</span>
                                <span className="admin-details-value">
                                    {admin ? `${admin.first_name || ''} ${admin.last_name || ''}`.trim() : 'N/A'}
                                </span>
                            </div>
                            <div className="admin-details-info-row">
                                <span className="admin-details-label">Username:</span>
                                <span className="admin-details-value">{admin?.username || 'N/A'}</span>
                            </div>
                            <div className="admin-details-info-row">
                                <span className="admin-details-label">Email:</span>
                                <span className="admin-details-value">{admin?.email || 'N/A'}</span>
                            </div>
                            {admin?.title && (
                                <div className="admin-details-info-row">
                                    <span className="admin-details-label">Title:</span>
                                    <span className="admin-details-value">{admin.title}</span>
                                </div>
                            )}
                        </div>

                        {/* Column 2 */}
                        <div className="admin-details-info-column">
                            <div className="admin-details-info-row">
                                <span className="admin-details-label">Home Phone:</span>
                                <span className="admin-details-value">{admin?.home_phone || admin?.phone || 'N/A'}</span>
                            </div>
                            <div className="admin-details-info-row">
                                <span className="admin-details-label">Cell Phone:</span>
                                <span className="admin-details-value">{admin?.cell_phone || 'N/A'}</span>
                            </div>
                            <div className="admin-details-info-row">
                                <span className="admin-details-label">Fax:</span>
                                <span className="admin-details-value">{admin?.fax || 'N/A'}</span>
                            </div>
                            <div className="admin-details-info-row">
                                <span className="admin-details-label">Status:</span>
                                <span className="admin-details-value">
                                    {getStatusBadge(admin?.status)}
                                </span>
                            </div>
                            <div className="admin-details-info-row">
                                <span className="admin-details-label">2FA:</span>
                                <span className="admin-details-value">
                                    {admin?.two_way_auth || (admin?.is_two_way_auth ? 'Enabled' : 'Disabled') || 'N/A'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Address Section if available */}
                    {(admin?.address_line_1 || admin?.city || admin?.state) && (
                        <div className="admin-details-address-row">
                            <span className="admin-details-label" style={{ minWidth: '100px' }}>Address:</span>
                            <span className="admin-details-value">
                                {[
                                    admin?.address_line_1,
                                    admin?.address_line_2,
                                    admin?.city,
                                    admin?.state,
                                    admin?.zip_code
                                ].filter(Boolean).join(', ')}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </React.Fragment>
    );
};

export default AdminDetails;
